/**
 * File fields in JSON-submitted Alpaca forms.
 *
 * Alpaca's file field holds whatever was last pushed into it, and the legacy form code
 * pushes the input's DOM value — the browser's "C:\fakepath\name.pdf" placeholder — so
 * a form posted as JSON carries a placeholder and never the bytes. The backend's
 * JsonDataUrlFilesHandler expects each file field to be an object carrying the contents
 * as a data URL.
 */

export const FILE_FIELD_TYPE = 'file';

// The whole form travels as one JSON body and base64 grows each file by a third, so the
// server's request limit applies to the sum. Keep in step with MAX_CONTENT_LENGTH
// (config/dev_config.py) and client_max_body_size (docker/nginx/palaute.conf).
export const MAX_JSON_UPLOAD_MB = 36;
export const MAX_JSON_UPLOAD_BYTES = MAX_JSON_UPLOAD_MB * 1024 * 1024;

/** Bytes the chosen files would add to the request, before base64 growth. */
export const chosenFilesTotalBytes = (container) => (container
    ? [...container.querySelectorAll('input[type="file"]')]
        .reduce((total, input) => total + (input.files && input.files[0] ? input.files[0].size : 0), 0)
    : 0);

export const fileToBase64DataUrl = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
});

/**
 * The wire shape JsonDataUrlFilesHandler reads; null when there is no file to send.
 */
export const fileFieldPayload = async (file) => (file && file.name ? {
    name: file.name,
    _type: FILE_FIELD_TYPE,
    content_type: file.type,
    size: file.size,
    contents: await fileToBase64DataUrl(file),
} : null);

/**
 * Read the file inputs under `container` in which a file was chosen, keyed by input name.
 * An input left empty is not reported: on a form being edited its value is the description
 * of the file already stored, and overwriting that would read to the server as "cleared".
 * Inputs without a name are skipped too, since nothing could address them in the payload.
 */
export const readFileFields = async (container) => {
    if (!container) return {};
    const inputs = [...container.querySelectorAll('input[type="file"]')]
        .filter(input => input.name && input.files && input.files[0]);
    const entries = await Promise.all(inputs.map(async input => [
        input.name,
        await fileFieldPayload(input.files[0]),
    ]));
    return Object.fromEntries(entries.filter(([, payload]) => payload));
};

/**
 * Replace the placeholder strings in `data` with the file payloads read from the DOM, for the
 * inputs in which a file was chosen. Everything else in `data` is passed through untouched.
 */
export const withFileFields = async (data, container) => ({
    ...data,
    ...await readFileFields(container),
});
